import React, { useContext, useEffect, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import {
  Paper,
  Typography,
  LinearProgress,
  Box,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Button,
} from "@material-ui/core";
import { Close } from "@material-ui/icons";
import AuthUserContext from "../AuthUserContext";
import { firebase } from "../../firebase";

const ProfileCompletenessIndicator = ({
  fromRoyalties = false,
  onNavigate,
}) => {
  // Add a direct check of the URL as a fallback
  const isFromRoyalties =
    fromRoyalties || window.location.search.includes("fromRoyalties=true");

  const history = useHistory();
  const location = useLocation();

  // Check if we're currently on the account page
  const isAccountPage = location.pathname === "/account";

  const [profileData, setProfileData] = useState(null);
  const [completionPercentage, setCompletionPercentage] = useState(0);
  const [missingFields, setMissingFields] = useState([]);
  const user = useContext(AuthUserContext);

  // Use the provided onNavigate function or default to using history
  const handleNavigation = () => {
    if (onNavigate) {
      onNavigate();
    } else {
      history.push(isFromRoyalties ? "/royalties" : "/account");
    }
  };

  const checkProfileCompleteness = (userData) => {
    if (!userData) return;

    // Calculate completion percentage and missing fields
    const requiredFields = [
      { key: "firstnames", label: "Nombre" },
      { key: "lastnames", label: "Apellido" },
      { key: "country", label: "País" },
      { key: "phone", label: "Teléfono" },
    ];

    const missing = [];
    let completed = 0;

    requiredFields.forEach((field) => {
      if (userData[field.key]) {
        completed++;
      } else {
        missing.push(field.label);
      }
    });

    setCompletionPercentage((completed / requiredFields.length) * 100);
    setMissingFields(missing);
  };

  useEffect(() => {
    const fetchProfileData = async () => {
      try {
        const userRef = firebase.db.ref(`users/${user.uid}`);
        const snapshot = await userRef.once("value");
        const userData = snapshot.val();
        setProfileData(userData);

        checkProfileCompleteness(userData);
      } catch (error) {
        console.error("Error fetching profile data:", error);
      }
    };

    if (user && user.uid) {
      fetchProfileData();
    }
  }, [user]);

  // Add a real-time listener for profile changes
  useEffect(() => {
    if (!user || !user.uid) return;

    const userRef = firebase.db.ref(`users/${user.uid}`);

    // Set up a real-time listener
    const profileListener = userRef.on("value", (snapshot) => {
      const userData = snapshot.val();
      setProfileData(userData);
      checkProfileCompleteness(userData);
    });

    // Clean up the listener when component unmounts
    return () => {
      userRef.off("value", profileListener);
    };
  }, [user]);
  // Only render the component if there are missing fields
  if (!profileData || missingFields.length === 0) {
    return null;
  }

  return (
    <Paper
      style={{ padding: 16, marginBottom: 24, backgroundColor: "#fff3e0" }}
    >
      <Typography variant="h6" gutterBottom>
        Perfil incompleto
      </Typography>

      <Box display="flex" alignItems="center" mb={1}>
        <Box width="100%" mr={1}>
          <LinearProgress
            variant="determinate"
            value={completionPercentage}
            color="secondary"
          />
        </Box>
        <Box minWidth={35}>
          <Typography variant="body2" color="textSecondary">
            {Math.round(completionPercentage)}%
          </Typography>
        </Box>
      </Box>

      <Typography variant="body2" color="error" paragraph>
        {isFromRoyalties
          ? "Completa los siguientes campos para poder solicitar pagos:"
          : "Para solicitar pagos, completa los siguientes campos:"}
      </Typography>

      <List dense>
        {missingFields.map((field, index) => (
          <ListItem key={index}>
            <ListItemIcon>
              <Close color="error" />
            </ListItemIcon>
            <ListItemText primary={field} />
          </ListItem>
        ))}
      </List>

      {/* Only show the button if we're not already on the account page */}
      {!isAccountPage && (
        <Button
          variant="contained"
          color="primary"
          size="small"
          onClick={handleNavigation}
        >
          {isFromRoyalties ? "Volver a Pagos" : "Completar mi perfil"}
        </Button>
      )}
    </Paper>
  );
};

export default ProfileCompletenessIndicator;
